import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import StaffService from "../../services/Staff/StaffService";
import {
  DollarSign,
  TrendingUp,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  Search,
  RotateCw,
  Download,
  X,
  ChevronLeft,
  ChevronRight,
  Inbox
} from "lucide-react";
import { Toast } from "../ui/Toast";

function StaffFinanceiro() {
  const [financeiroData, setFinanceiroData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState("");
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina] = useState(10);

  const toastRef = useRef(null);
  const staffService = useRef(new StaffService()).current;

  const carregarFinanceiro = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Busca financeiro geral
      let listaFin = [];
      const resFin = await staffService.getFinanceiroGeral();
      if (resFin.ok) {
        const data = await resFin.json();
        listaFin = Array.isArray(data) ? data : (data?.results || data?.corridas || []);
      }

      // 2. Se a lista de financeiro direto vier vazia ou precisar mesclar com dados de corridas
      if (listaFin.length === 0) {
        const resCorridas = await staffService.getTodasCorridas();
        if (resCorridas.ok) {
          const dataCorridas = await resCorridas.json();
          const corridas = Array.isArray(dataCorridas) ? dataCorridas : (dataCorridas?.results || []);
          listaFin = corridas.map((c) => {
            const totalArrecadado = Number(c.total_arrecadado || c.arrecadado || c.valor_total || 0);
            const inscricoes = Number(c.total_inscritos || c.qtd_inscritos || c.inscritos || 0);
            const taxaEmReais = Number(c.taxa_valor || c.valor_taxa || c.taxa || c.valor_fixo || 5.00);

            // Receita Urbrace calculada por taxa em real por inscrição
            const valorUrbrace = Number(c.receita_urbrace || (inscricoes * taxaEmReais));
            const repasse = Math.max(0, totalArrecadado - valorUrbrace);

            return {
              id: c.id_corrida || c.id,
              nome_corrida: c.nome_corrida || c.nome,
              organizador: c.organizador_nome || c.organizador || "Organizador",
              total_inscricoes: inscricoes,
              total_arrecadado: totalArrecadado,
              taxa_real: taxaEmReais,
              receita_urbrace: valorUrbrace,
              repasse_organizador: repasse,
              status: c.status_evento || c.status || "Ativo"
            };
          });
        }
      } else {
        // Normaliza campos caso venham da API de financeiro
        listaFin = listaFin.map((item) => {
          const totalArrecadado = Number(item.total_arrecadado || item.arrecadado || item.valor_bruto || 0);
          const inscricoes = Number(item.total_inscricoes || item.total_inscritos || item.inscritos || 0);
          const taxaEmReais = Number(item.taxa_valor || item.valor_taxa || item.taxa || item.valor_fixo || 5.00);
          const receitaUrbrace = Number(item.receita_urbrace || item.taxa_urbrace || (inscricoes * taxaEmReais));
          const repasse = Number(item.repasse_organizador || item.valor_liquido || Math.max(0, totalArrecadado - receitaUrbrace));

          return {
            ...item,
            id: item.id_corrida || item.id,
            nome_corrida: item.nome_corrida || item.corrida_nome || item.nome || `Corrida #${item.id || ""}`,
            organizador: item.organizador_nome || item.organizador || "—",
            total_inscricoes: inscricoes,
            total_arrecadado: totalArrecadado,
            taxa_real: taxaEmReais,
            receita_urbrace: receitaUrbrace,
            repasse_organizador: repasse,
            status: item.status || "Processado"
          };
        });
      }

      setFinanceiroData(listaFin);
    } catch (error) {
      console.error("Erro ao carregar dados financeiros do staff:", error);
      toastRef.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Não foi possível carregar os dados financeiros."
      });
    } finally {
      setLoading(false);
    }
  }, [staffService]);

  useEffect(() => {
    carregarFinanceiro();
  }, [carregarFinanceiro]);

  const formatCurrency = (val) => {
    return Number(val || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  };

  // Cálculos dos KPIs consolidados
  const totalBruto = financeiroData.reduce((acc, curr) => acc + Number(curr.total_arrecadado || 0), 0);
  const totalUrbrace = financeiroData.reduce((acc, curr) => acc + Number(curr.receita_urbrace || 0), 0);
  const totalRepasse = financeiroData.reduce((acc, curr) => acc + Number(curr.repasse_organizador || 0), 0);
  const totalInscricoes = financeiroData.reduce((acc, curr) => acc + Number(curr.total_inscricoes || 0), 0);

  // Filtro
  const dadosFiltrados = useMemo(() => {
    if (!globalFilter.trim()) return financeiroData;
    const term = globalFilter.toLowerCase();
    return financeiroData.filter((item) =>
      (item.nome_corrida || "").toLowerCase().includes(term) ||
      (item.organizador || "").toLowerCase().includes(term) ||
      String(item.id || "").includes(term)
    );
  }, [financeiroData, globalFilter]);

  // Paginação
  const totalPaginas = Math.ceil(dadosFiltrados.length / itensPorPagina) || 1;
  const inicioIndice = (paginaAtual - 1) * itensPorPagina;
  const dadosPaginados = dadosFiltrados.slice(inicioIndice, inicioIndice + itensPorPagina);
  const totalMostrado = dadosFiltrados.length;
  const inicioItem = totalMostrado > 0 ? inicioIndice + 1 : 0;
  const fimItem = Math.min(inicioIndice + itensPorPagina, totalMostrado);

  const exportarCSV = () => {
    if (dadosFiltrados.length === 0) return;
    const cabecalho = "ID;Corrida;Organizador;Inscricoes;Total Bruto;Taxa Urbrace;Receita Urbrace;Liquido Organizador;Status\n";
    const linhas = dadosFiltrados
      .map(
        (r) =>
          `"${r.id}";"${r.nome_corrida}";"${r.organizador}";${r.total_inscricoes};"${r.total_arrecadado}";"${r.taxa_real}";"${r.receita_urbrace}";"${r.repasse_organizador}";"${r.status}"`
      )
      .join("\n");
    const blob = new Blob([cabecalho + linhas], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `staff_financeiro_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toastRef.current?.show({
      severity: "success",
      summary: "Sucesso",
      detail: "Relatório CSV gerado com sucesso!"
    });
  };

  return (
    <div className="flex flex-col gap-6 w-full text-urb-text">
      <Toast ref={toastRef} />

      {/* KPIs de Financeiro da Plataforma */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Bruto Movimentado */}
        <div className="bg-urb-card border border-urb-border rounded-2xl p-5 flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400 font-bold uppercase tracking-wider">
              Volume Bruto Total
            </span>
            <div className="w-9 h-9 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-blue-400" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-2xl font-bold text-white tracking-tight">
              {loading ? (
                <div className="h-7 w-28 bg-slate-800 rounded animate-pulse"></div>
              ) : (
                formatCurrency(totalBruto)
              )}
            </div>
            <span className="text-xs text-slate-400 mt-1 flex items-center gap-1">
              <ArrowUpRight className="w-3.5 h-3.5 text-blue-400 inline shrink-0" />
              Total processado pela plataforma
            </span>
          </div>
        </div>

        {/* Receita Urbrace (Taxas em Reais) */}
        <div className="bg-urb-card border border-urb-primary/30 rounded-2xl p-5 flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-urb-primary">
              Receita Urbrace (Taxas)
            </span>
            <div className="w-9 h-9 rounded-xl bg-urb-primary/10 border border-urb-primary/30 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-urb-primary" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-2xl font-bold text-urb-primary tracking-tight">
              {loading ? (
                <div className="h-7 w-28 bg-slate-800 rounded animate-pulse"></div>
              ) : (
                formatCurrency(totalUrbrace)
              )}
            </div>
            <span className="text-xs text-slate-400 mt-1 flex items-center gap-1">
              Total de taxas em Reais retidas
            </span>
          </div>
        </div>

        {/* Repasse aos Organizadores */}
        <div className="bg-urb-card border border-urb-border rounded-2xl p-5 flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400 font-bold uppercase tracking-wider">
              Repasse Organizadores
            </span>
            <div className="w-9 h-9 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-purple-400" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-2xl font-bold text-white tracking-tight">
              {loading ? (
                <div className="h-7 w-28 bg-slate-800 rounded animate-pulse"></div>
              ) : (
                formatCurrency(totalRepasse)
              )}
            </div>
            <span className="text-xs text-slate-400 mt-1 flex items-center gap-1">
              <ArrowDownRight className="w-3.5 h-3.5 text-purple-400 inline shrink-0" />
              Valores líquidos dos organizadores
            </span>
          </div>
        </div>

        {/* Total Inscrições Faturadas */}
        <div className="bg-urb-card border border-urb-border rounded-2xl p-5 flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400 font-bold uppercase tracking-wider">
              Inscrições Faturadas
            </span>
            <div className="w-9 h-9 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-amber-400" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-2xl font-bold text-white tracking-tight">
              {loading ? (
                <div className="h-7 w-16 bg-slate-800 rounded animate-pulse"></div>
              ) : (
                totalInscricoes
              )}
            </div>
            <span className="text-xs text-slate-400 mt-1">
              Em {financeiroData.length} evento{financeiroData.length === 1 ? "" : "s"} registrados
            </span>
          </div>
        </div>
      </div>

      {/* Tabela Financeira Analítica */}
      <div className="bg-urb-card border border-urb-border rounded-2xl p-4 sm:p-6 shadow-sm">
        {/* Barra superior com filtro e ações */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div className="relative flex items-center w-full sm:w-80">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 pointer-events-none" />
            <input
              type="text"
              value={globalFilter}
              onChange={(e) => {
                setGlobalFilter(e.target.value);
                setPaginaAtual(1);
              }}
              placeholder="Buscar corrida ou organizador..."
              className="w-full pl-9 pr-8 py-2 bg-urb-card-light border border-urb-border rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-urb-primary/60 transition-colors"
            />
            {globalFilter && (
              <button
                type="button"
                onClick={() => setGlobalFilter("")}
                className="absolute right-2.5 text-slate-400 hover:text-white bg-transparent border-none cursor-pointer"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={carregarFinanceiro}
              disabled={loading}
              className="inline-flex items-center gap-2 px-3 py-2 bg-urb-card-light hover:bg-urb-card border border-urb-border hover:border-urb-border/80 rounded-xl text-xs font-semibold text-slate-300 hover:text-white transition-colors cursor-pointer"
            >
              <RotateCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              <span>Atualizar</span>
            </button>
            <button
              type="button"
              onClick={exportarCSV}
              disabled={loading || financeiroData.length === 0}
              className="inline-flex items-center gap-2 px-3 py-2 bg-urb-primary/10 hover:bg-urb-primary/20 border border-urb-primary/30 rounded-xl text-xs font-semibold text-urb-primary transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Exportar CSV</span>
            </button>
          </div>
        </div>

        {/* Tabela de Dados */}
        <div className="overflow-x-auto rounded-xl border border-urb-border/50">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-urb-border bg-urb-card-light/40 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                <th className="py-3 px-3 w-16">ID</th>
                <th className="py-3 px-4">Corrida</th>
                <th className="py-3 px-4">Organizador</th>
                <th className="py-3 px-4 text-center">Inscrições</th>
                <th className="py-3 px-4 text-right">Total Bruto</th>
                <th className="py-3 px-4 text-right">Taxa Urbrace (por Inscrição)</th>
                <th className="py-3 px-4 text-right">Receita Urbrace</th>
                <th className="py-3 px-4 text-right">Líquido Organizador</th>
                <th className="py-3 px-4 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-urb-border/30">
              {loading ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={idx} className="animate-pulse">
                    <td className="py-3 px-3"><div className="h-5 w-8 bg-slate-800 rounded"></div></td>
                    <td className="py-3 px-4"><div className="h-5 w-32 bg-slate-800 rounded"></div></td>
                    <td className="py-3 px-4"><div className="h-5 w-24 bg-slate-800 rounded"></div></td>
                    <td className="py-3 px-4 text-center"><div className="h-5 w-10 bg-slate-800 rounded mx-auto"></div></td>
                    <td className="py-3 px-4 text-right"><div className="h-5 w-20 bg-slate-800 rounded ml-auto"></div></td>
                    <td className="py-3 px-4 text-right"><div className="h-5 w-16 bg-slate-800 rounded ml-auto"></div></td>
                    <td className="py-3 px-4 text-right"><div className="h-5 w-20 bg-slate-800 rounded ml-auto"></div></td>
                    <td className="py-3 px-4 text-right"><div className="h-5 w-20 bg-slate-800 rounded ml-auto"></div></td>
                    <td className="py-3 px-4 text-center"><div className="h-5 w-14 bg-slate-800 rounded mx-auto"></div></td>
                  </tr>
                ))
              ) : dadosPaginados.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-slate-400">
                    <Inbox className="w-10 h-10 mx-auto mb-2 text-slate-500" />
                    <p className="text-sm">Nenhum dado financeiro encontrado.</p>
                  </td>
                </tr>
              ) : (
                dadosPaginados.map((row) => (
                  <tr key={row.id} className="hover:bg-urb-card-light/40 transition-colors">
                    <td className="py-3 px-3 text-xs text-slate-500 font-mono">
                      #{row.id}
                    </td>
                    <td className="py-3 px-4 font-semibold text-white">
                      {row.nome_corrida}
                    </td>
                    <td className="py-3 px-4 text-slate-300">
                      {row.organizador}
                    </td>
                    <td className="py-3 px-4 text-center font-medium text-slate-200">
                      {row.total_inscricoes}
                    </td>
                    <td className="py-3 px-4 text-right font-medium text-white">
                      {formatCurrency(row.total_arrecadado)}
                    </td>
                    <td className="py-3 px-4 text-right font-semibold text-slate-300">
                      {formatCurrency(row.taxa_real)}
                    </td>
                    <td className="py-3 px-4 text-right font-bold text-urb-primary">
                      {formatCurrency(row.receita_urbrace)}
                    </td>
                    <td className="py-3 px-4 text-right text-slate-300">
                      {formatCurrency(row.repasse_organizador)}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                        {row.status || "Ativo"}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer com Paginação */}
        {!loading && dadosFiltrados.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 mt-3 border-t border-urb-border/40">
            <span className="text-xs text-slate-400">
              Mostrando {inicioItem} a {fimItem} de {totalMostrado} registros
            </span>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPaginaAtual((prev) => Math.max(prev - 1, 1))}
                disabled={paginaAtual === 1}
                className="w-8 h-8 rounded-lg flex items-center justify-center bg-urb-card-light border border-urb-border text-slate-300 hover:text-white hover:border-urb-primary/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {Array.from({ length: totalPaginas }, (_, i) => i + 1).map((pg) => (
                <button
                  key={pg}
                  type="button"
                  onClick={() => setPaginaAtual(pg)}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold transition-colors cursor-pointer border ${
                    pg === paginaAtual
                      ? "bg-urb-primary text-black border-urb-primary"
                      : "bg-urb-card-light border-urb-border text-slate-300 hover:text-white hover:border-urb-primary/40"
                  }`}
                >
                  {pg}
                </button>
              ))}

              <button
                type="button"
                onClick={() => setPaginaAtual((prev) => Math.min(prev + 1, totalPaginas))}
                disabled={paginaAtual === totalPaginas}
                className="w-8 h-8 rounded-lg flex items-center justify-center bg-urb-card-light border border-urb-border text-slate-300 hover:text-white hover:border-urb-primary/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default StaffFinanceiro;
